import React, { Component } from 'react';
import { Form } from 'react-final-form';
import Axios from 'axios';
import { FORM_ERROR } from 'final-form';
import { renderErrors } from '@/components/common/Forms/Utils';
import showToast from '@/components/common/Toast';
import { CheckboxField } from '@/components/common/Forms';
import styles from './StudentFeatures.module.scss';
import { handleRadio } from '../RadioGroup';
import {
  ActionButtonsWithError,
  handleSelect,
  handleCheckbox,
  Header,
  SelectField,
} from '../Util';
import {
  RestartClassroomActivities,
  RestartLessonOutlines,
  RestartLessonReviewGames,
  RestartReadingChecks,
  RestartVideoActivities,
  RestartVocabularyGames,
} from './Util';
import Text from '../../../../ui/Text';

interface Settings {
  classroom_activity_retry?: boolean;
  lesson_game_retry?: boolean;
  lesson_outline_retry?: boolean;
  lock_after_turn_in?: boolean;
  reading_game_retry?: boolean;
  show_page_numbers?: boolean;
  student_default_investigation?: string;
  student_view_lesson_game_off?: boolean;
  student_view_lesson_off?: boolean;
  text_audio_mode?: string;
  video_activity_retry?: boolean;
  vocab_game_retry?: boolean;
}

interface Props {
  active?: boolean;
  handleCancel?: () => void;
  narrationEnabled: boolean;
  programHasClassroomActivity: boolean;
  programHasLessonOutline: boolean;
  programHasVideoActivity: boolean;
  programHasVocabGames: boolean;
  reviewGameSetting: boolean;
  scienceProgram: boolean;
  settings?: Settings;
  showLimitedSettings?: boolean;
  stafferPath: string;
  streamEnabled: boolean;
  studentsPath?: string;
  updateState?: (next: { settings: Settings }) => void;
}

interface State {
  submitting: boolean;
}

const defaultSettings = (scienceProgram: boolean, narrationEnabled: boolean): Settings => (
  {
    lesson_game_retry: false,
    reading_game_retry: true,
    show_page_numbers: true,
    student_default_investigation: scienceProgram ? 'fast_track' : 'master',
    student_view_lesson_game_off: false,
    student_view_lesson_off: false,
    text_audio_mode: narrationEnabled ? 'narration' : 'text_to_speech',
    video_activity_retry: false,
    vocab_game_retry: true,
  }
);

export default class StudentFeatures extends Component<Props, State> {
  static defaultProps: Partial<Props> = {
    active: false,
    settings: {
      student_view_lesson_off: false,
    },
    showLimitedSettings: false,
    updateState: () => {},
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      submitting: false,
    };
  }

  getInitialValues() {
    const { narrationEnabled, scienceProgram, settings } = this.props;
    return Object.assign(
      {},
      defaultSettings(scienceProgram, narrationEnabled),
      settings,
    );
  }

  handleSubmit = async (values: Settings) => {
    const { stafferPath, updateState } = this.props;
    this.setState({ submitting: true });

    return Axios
      .put(stafferPath, values)
      .then((response) => {
        const { settings } = response.data.data;
        showToast('Save successful');
        this.setState({ submitting: false });
        updateState?.({ settings });
      })
      .catch(error => this.handleSubmitError(error));
  };

  handleSubmitError(error: any) {
    this.setState({ submitting: false });
    showToast('Save failed', { msgType: 'error' });
    return { [FORM_ERROR]: renderErrors(error.response.data.errors) };
  }

  render() {
    const {
      active,
      handleCancel,
      narrationEnabled,
      programHasClassroomActivity,
      programHasLessonOutline,
      programHasVideoActivity,
      programHasVocabGames,
      reviewGameSetting,
      showLimitedSettings,
      streamEnabled,
      studentsPath,
    } = this.props;
    const { submitting } = this.state;

    if (!active) return null;

    return (
      <div className={streamEnabled ? undefined : styles.withInvestigationNotebook}>
        <Form
          initialValues={this.getInitialValues()}
          mutators={{
            handleCheckbox,
            handleRadio,
            handleSelect,
          }}
          render={({
            handleSubmit, submitError, values, form,
          }) => {
            const checkboxProps = {
              fieldClass: 'tw-flex tw-justify-end',
              labelClass: 'tw-w-full tw-font-bold',
              onChange: form.mutators.handleCheckbox,
              rowClass: 'tw-flex tw-justify-between',
            };

            return (
              <form onSubmit={handleSubmit}>
                <Header text="Reading Supports" />
                <div className="tw-mb-5 tw-flex tw-items-start tw-justify-between">
                  <div>
                    <Text size="sm">
                      Manage the following individual student settings on the Students Page:
                    </Text>
                    <ul className="tw-mt-2 tw-list-disc tw-pl-5">
                      <Text asChild size="sm"><li>Standard Level</li></Text>
                      <Text asChild size="sm"><li>Leveled Reading</li></Text>
                      <Text asChild size="sm"><li>Spanish</li></Text>
                      <Text asChild size="sm"><li>Text Read-Aloud</li></Text>
                      <Text asChild size="sm"><li>Main Ideas</li></Text>
                    </ul>
                  </div>
                  {studentsPath && (
                    <Text asChild size="sm">
                      <a
                        className="tw-ml-5 tw-whitespace-nowrap"
                        href={studentsPath}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Manage <i aria-hidden="true" className="fa fa-external-link tw-ml-1" />
                      </a>
                    </Text>
                  )}
                </div>
                <hr />
                <Header text="Reading Preferences" />
                {narrationEnabled && (
                  <div className="tw-mb-5">
                    <SelectField
                      rightAligned
                      label="Text Read-Aloud Voice"
                      name="text_audio_mode"
                      options={[
                        { label: 'Narration', value: 'narration' },
                        { label: 'Text to Speech', value: 'text_to_speech' },
                      ]}
                      onChange={form.mutators.handleSelect}
                    />
                    <Text size="sm">
                      Set to Narration to hear human-voice audio of text (Subject to availability).
                      Set to Text-to-Speech to highlight text while playing audio.
                    </Text>
                  </div>
                )}
                <div className="tw-mb-5">
                  <CheckboxField
                    {...checkboxProps}
                    useTextComponent
                    checked={values.show_page_numbers}
                    label="See Print Book Page Numbers"
                    name="show_page_numbers"
                  />
                  <Text size="sm">
                    Display page numbers in the section drop down for student text.
                  </Text>
                </div>
                {!showLimitedSettings
                  && (
                  <>
                    <hr />
                    <Header text="Allow Multiple Attempts" />

                    <RestartLessonReviewGames form={form} values={values} />

                    {programHasVocabGames
                      && <RestartVocabularyGames form={form} values={values} />
                    }

                    {reviewGameSetting
                      && <RestartReadingChecks form={form} values={values} />
                    }

                    {programHasVideoActivity
                      && <RestartVideoActivities form={form} values={values} />
                    }

                    {programHasClassroomActivity
                      && <RestartClassroomActivities form={form} values={values} />
                    }

                    {programHasLessonOutline
                      && <RestartLessonOutlines form={form} values={values} />
                    }

                    <div className="mb20">
                      <CheckboxField
                        {...checkboxProps}
                        useTextComponent
                        checked={values.lock_after_turn_in}
                        label="Lock after Turn In"
                        name="lock_after_turn_in"
                      />
                      <Text size="sm">
                        Select this to allow students to turn in each notebook section only once.
                        Once turned <br /> in, the notebook section will be in view only mode.
                      </Text>
                      <Text className="tw-mt-4 tw-italic" size="sm">
                        This setting does not apply to unit projects and slideshows.
                      </Text>
                    </div>
                  </>
                  )
                }

                <ActionButtonsWithError
                  handleCancel={handleCancel}
                  submitError={submitError}
                  submitting={submitting}
                />
              </form>
            );
          }}
          onSubmit={this.handleSubmit}
        />
      </div>
    );
  }
}
